import { t as tr } from "@/lib/i18n";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { Plus, Search, Workflow } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";

import { Button, cn, Input, Skeleton } from "@fox1ck-org/ui";

import { TemplateCard } from "@/components/canonical/blueprint/template-card";
import { ApplyTemplateWizard } from "@/components/canonical/blueprint/apply-wizard";
import {
  templateScopeLabel,
  useArchiveFlowTemplate,
  useInstantiateFlowTemplate,
  useListFlowTemplates,
} from "@/lib/services/flow-template-service";
import {
  TemplateScope,
  type FlowTemplate,
} from "@gen/tracker/v1/flow_template_pb";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { PageState } from "@fox1ck-org/ui";
import { failureText } from "@/lib/errors";

export default function FlowTemplatesListPage() {
  const navigate = useNavigate();
  const { motif } = useBrandAnswer();
  // Filter state lives in the URL so deep links and history navigation share
  // the same view; useTableParams is where every list in this app keeps it.
  const params = useTableParams();
  const search = params.search;
  const category = params.getFilter("category");
  const scopeRaw = params.getFilter("scope") || "all";
  const scopeFilter: TemplateScope | "all" =
    scopeRaw === "all" ? "all" : (Number(scopeRaw) as TemplateScope);

  const setSearch = params.setSearch;
  const setCategory = (v: string) => params.setFilter("category", v || null);
  const setScopeFilter = (v: TemplateScope | "all") =>
    params.setFilter("scope", v === "all" ? null : String(v));

  const { data, isLoading, error } = useListFlowTemplates({ search, category });
  const archive = useArchiveFlowTemplate();
  const instantiate = useInstantiateFlowTemplate();
  const [selected, setSelected] = useState<FlowTemplate | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const allItems = useMemo(() => data?.items ?? [], [data]);
  const items = useMemo(
    () => (scopeFilter === "all" ? allItems : allItems.filter((t) => t.scope === scopeFilter)),
    [allItems, scopeFilter],
  );

  const categories = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((t) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [allItems]);

  function openTemplate(t: FlowTemplate) {
    setSelected(t);
    setDrawerOpen(true);
  }

  async function handleArchive(t: FlowTemplate) {
    if (!confirm(tr("ft.confirmArchive", { name: t.name }))) return;
    try {
      await archive.mutateAsync(t.id);
      toast.success(tr("ft.archived", { name: t.name }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("flows.archiveFailed"));
    }
  }

  async function applyBindings(bindings: Record<string, string>) {
    if (!selected) return;
    try {
      const flow = await instantiate.mutateAsync({
        templateId: selected.id,
        bindings,
      });
      toast.success(tr("ft.created", { name: flow.name }));
      setDrawerOpen(false);
      // Land the author in the editor on the freshly minted flow — the next
      // step is always reviewing/binding it to a campaign, not staying in the
      // gallery.
      navigate(`/admin/flows/${flow.id}/edit`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("ft.createFailed"));
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
      <div className="flex flex-col gap-4">

      <header className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Workflow className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-[0.08em]">
                {tr("ft.eyebrow")}
              </span>
            </div>
            <h1 className="mt-2 text-page-title text-foreground">
              {tr("ft.title")}
            </h1>
            <p className="mt-1 max-w-160 text-sm leading-relaxed text-muted-foreground">
              {tr("ft.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="primary" size="md" className="gap-2">
              <Link to="/admin/flow-templates/new">
                <Plus className="h-4 w-4" /> {tr("ft.new")}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full md:w-80">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.75}
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            font="sans"
            placeholder={tr("ft.search")}
            className="pl-8"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {tr("tpl.scope")}
          </span>
          {(["all", TemplateScope.WORKSPACE, TemplateScope.ORG, TemplateScope.GLOBAL] as const).map((s) => (
            <FilterChip
              key={String(s)}
              active={scopeFilter === s}
              onClick={() => setScopeFilter(s)}
            >
              {s === "all" ? tr("ft.all") : templateScopeLabel(s as TemplateScope)}
            </FilterChip>
          ))}
        </div>

        {categories.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="ml-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              {tr("tpl.category")}
            </span>
            <FilterChip active={category === ""} onClick={() => setCategory("")}>
              {tr("ft.all")}
            </FilterChip>
            {categories.map((c) => (
              <FilterChip key={c} active={category === c} onClick={() => setCategory(c)}>
                {c}
              </FilterChip>
            ))}
          </div>
        ) : null}
      </div>

      {error ? (
        <PageState mode="error" title={tr("ft.loadError")} error={failureText(error)} />
      ) : null}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-55 w-full rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <PageState
          mode="empty"
          motif={search || category ? null : motif}
          icon={<Workflow className="size-5" strokeWidth={1.5} />}
          title={search || category ? tr("ft.noMatch") : tr("ft.empty")}
          description={
            search || category
              ? tr("ft.noMatch.hint")
              : tr("ft.empty.hint")
          }
          action={
            search || category ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setSearch("");
                  setCategory("");
                  setScopeFilter("all");
                }}
              >
                {tr("ft.clear")}
              </Button>
            ) : (
              <Button asChild variant="primary">
                <Link to="/admin/flow-templates/new">{tr("ft.first")}</Link>
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onApply={() => openTemplate(t)}
              onArchive={() => handleArchive(t)}
            />
          ))}
        </div>
      )}

      <ApplyTemplateWizard
        open={drawerOpen}
        template={selected}
        onOpenChange={setDrawerOpen}
        onApply={applyBindings}
        isApplying={instantiate.isPending}
      />
      </div>
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="switch"
      aria-checked={active}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-card text-muted-foreground hover:border-ring/50 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
